import { cn } from '@sistemas/ui'
import { Pin, PinOff } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import type { NavPins } from '@/lib/nav-pins'
import { matchesNavItem, type NavItem } from '@/lib/navigation'

export interface PinControls {
  readonly pinned: boolean
  readonly onToggle: () => void
  /** A linha é a do topo (junto do Dashboard), não a do grupo. */
  readonly inTop?: boolean
  /** Só no topo: Alt+↑/↓ com o foco no item troca com o vizinho. */
  readonly onMove?: (delta: -1 | 1) => void
}

/** Controles de um fixado no topo: soltar e Alt+↑/↓ (na ponta, nada). */
export function pinControls(
  to: string,
  pins: Pick<NavPins, 'toggle' | 'move'>,
  index: number,
  count: number,
): PinControls {
  return {
    pinned: true,
    inTop: true,
    onToggle: () => pins.toggle(to),
    onMove: (delta) => {
      const target = index + delta
      if (target >= 0 && target < count) pins.move(to, delta)
    },
  }
}

/**
 * Um item da sidebar. O pin fica à direita, pequeno e apagado: o item em si é
 * o alvo do clique, e reordenar os fixados é arrastando (ou Alt+↑/↓).
 *
 * `suppressActive`: o item está fixado e esta é a cópia do grupo — o destaque
 * de "página atual" fica só na zona de fixados, para não marcar duas linhas.
 */
export function NavLink({
  item,
  pathname,
  collapsed = false,
  suppressActive = false,
  pin,
  rootRef,
  rootProps,
}: {
  item: NavItem
  pathname: string
  collapsed?: boolean
  suppressActive?: boolean
  pin?: PinControls
  rootRef?: React.Ref<HTMLLIElement>
  rootProps?: React.LiHTMLAttributes<HTMLLIElement>
}) {
  const Icon = item.icon
  const active = !suppressActive && matchesNavItem(pathname, item.to)
  const onMove = pin?.onMove
  const onKeyDown = onMove
    ? (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return
        e.preventDefault()
        onMove(e.key === 'ArrowUp' ? -1 : 1)
      }
    : undefined
  return (
    <li ref={rootRef} {...rootProps} className={cn('group/item relative', rootProps?.className)}>
      <Link
        to={item.to}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        aria-keyshortcuts={onMove ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
        onKeyDown={onKeyDown}
        draggable={false}
        className={cn(
          'relative flex h-8 items-center gap-2.5 rounded-md px-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          collapsed && 'justify-center px-0',
          active
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
        )}
        title={collapsed ? item.label : undefined}
      >
        {active && (
          <span className="absolute -left-2 top-1.5 h-5 w-0.5 rounded-r-full bg-primary" />
        )}
        <Icon className={cn('h-4 w-4 shrink-0', active && 'text-primary')} />
        {!collapsed && <span className={cn('truncate', pin && 'pr-5')}>{item.label}</span>}
      </Link>
      {pin && !collapsed && (
        // Fora do link (botão dentro de link não é HTML válido). Aparece ao
        // passar o mouse ou com foco do teclado; fixado no grupo, o pin fica
        // à vista, bem apagado, para dizer que ele também está no topo.
        <button
          type="button"
          onClick={pin.onToggle}
          // Clicar no pin não pode começar um arrasto da linha.
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          aria-pressed={pin.pinned}
          aria-label={pin.pinned ? `Soltar ${item.label} do topo` : `Fixar ${item.label} no topo`}
          title={pin.pinned ? 'Soltar do topo' : 'Fixar no topo'}
          className={cn(
            'absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/item:opacity-100',
            pin.pinned && !pin.inTop && 'opacity-30',
          )}
        >
          {/* No topo, soltar; no grupo, o pin cheio diz que ele também está lá em cima. */}
          {pin.pinned && pin.inTop ? (
            <PinOff className="h-3 w-3" />
          ) : (
            <Pin className={cn('h-3 w-3', pin.pinned && 'fill-current')} />
          )}
        </button>
      )}
    </li>
  )
}
